/**
 * 効果音・環境音の管理。
 *
 * すべて Web Audio API によるプロシージャル生成。外部音源は使わない。
 * 生成パラメータの解説は src/audio/README.md にまとめてある。
 *
 * 使い方:
 *   const audio = new AudioManager();
 *   audio.unlock();               // 最初のユーザー操作で 1 回だけ
 *   audio.play('cable_click');
 */

import { createNoiseBuffer, playArpeggio, playNoise, playTone } from './synth';

export type SoundId =
  | 'footstep_carpet'
  | 'footstep_tile'
  | 'footstep_stair'
  | 'examine'
  | 'confirm'
  | 'cancel'
  | 'notebook_open'
  | 'notebook_close'
  | 'cable_click'
  | 'link_up'
  | 'warning'
  | 'error'
  | 'terminal_type'
  | 'terminal_done'
  | 'notify'
  | 'chapter_clear'
  | 'clue_found'
  | 'ui_hover'
  | 'door';

export interface VolumeSettings {
  master: number;
  bgm: number;
  sfx: number;
  ambient: number;
}

export const DEFAULT_VOLUMES: VolumeSettings = {
  master: 0.8,
  bgm: 0.5,
  sfx: 0.8,
  ambient: 0.35,
};

type Bus = 'sfx' | 'bgm' | 'ambient';

export class AudioManager {
  private ctx: AudioContext | null = null;
  private masterGain: GainNode | null = null;
  private busGains: Record<Bus, GainNode> | null = null;
  private noiseBuffer: AudioBuffer | null = null;
  private ambienceStarted = false;
  private ambienceNodes: AudioNode[] = [];
  private volumes: VolumeSettings = { ...DEFAULT_VOLUMES };
  private lastPlayedAt = new Map<string, number>();

  get isReady(): boolean {
    return this.ctx !== null && this.ctx.state === 'running';
  }

  /**
   * 最初のユーザー操作から呼ぶ。
   * ブラウザの自動再生制限のため、それ以前に AudioContext を作らない。
   */
  unlock(): void {
    if (!this.ctx) {
      try {
        this.ctx = new AudioContext();
      } catch (error) {
        console.warn('[audio] AudioContext を作成できませんでした', error);
        return;
      }
      this.masterGain = this.ctx.createGain();
      this.masterGain.connect(this.ctx.destination);

      const makeBus = (): GainNode => {
        const gain = (this.ctx as AudioContext).createGain();
        gain.connect(this.masterGain as GainNode);
        return gain;
      };
      this.busGains = { sfx: makeBus(), bgm: makeBus(), ambient: makeBus() };
      this.noiseBuffer = createNoiseBuffer(this.ctx, 2);
      this.applyVolumes();
    }
    if (this.ctx.state === 'suspended') {
      void this.ctx.resume().catch(() => undefined);
    }
  }

  setVolumes(volumes: Partial<VolumeSettings>): void {
    this.volumes = { ...this.volumes, ...volumes };
    this.applyVolumes();
  }

  getVolumes(): VolumeSettings {
    return { ...this.volumes };
  }

  private applyVolumes(): void {
    if (!this.masterGain || !this.busGains || !this.ctx) return;
    const now = this.ctx.currentTime;
    this.masterGain.gain.setTargetAtTime(this.volumes.master, now, 0.02);
    this.busGains.sfx.gain.setTargetAtTime(this.volumes.sfx, now, 0.02);
    this.busGains.bgm.gain.setTargetAtTime(this.volumes.bgm, now, 0.02);
    this.busGains.ambient.gain.setTargetAtTime(this.volumes.ambient, now, 0.05);
  }

  /** 空調のノイズループ。BGM は今回は無し */
  startAmbience(): void {
    if (this.ambienceStarted || !this.ctx || !this.busGains || !this.noiseBuffer) return;
    this.ambienceStarted = true;

    const ctx = this.ctx;
    const source = ctx.createBufferSource();
    source.buffer = this.noiseBuffer;
    source.loop = true;

    // 低い方だけを通して「ゴーッ」という空調音にする
    const lowpass = ctx.createBiquadFilter();
    lowpass.type = 'lowpass';
    lowpass.frequency.value = 340;
    lowpass.Q.value = 0.6;

    const highpass = ctx.createBiquadFilter();
    highpass.type = 'highpass';
    highpass.frequency.value = 60;

    const gain = ctx.createGain();
    gain.gain.value = 0.12;

    // ゆっくりした揺らぎを与えて機械音らしくする
    const lfo = ctx.createOscillator();
    lfo.frequency.value = 0.07;
    const lfoGain = ctx.createGain();
    lfoGain.gain.value = 0.035;
    lfo.connect(lfoGain);
    lfoGain.connect(gain.gain);

    source.connect(lowpass);
    lowpass.connect(highpass);
    highpass.connect(gain);
    gain.connect(this.busGains.ambient);

    source.start();
    lfo.start();
    this.ambienceNodes = [source, lfo, gain];
  }

  stopAmbience(): void {
    for (const node of this.ambienceNodes) {
      if ('stop' in node && typeof (node as AudioScheduledSourceNode).stop === 'function') {
        try {
          (node as AudioScheduledSourceNode).stop();
        } catch {
          /* すでに停止している場合は無視 */
        }
      }
      node.disconnect();
    }
    this.ambienceNodes = [];
    this.ambienceStarted = false;
  }

  /**
   * 効果音を鳴らす。
   * throttleMs を指定すると、その間隔より短い連続再生を間引く（足音やタイプ音向け）。
   */
  play(id: SoundId, options: { throttleMs?: number } = {}): void {
    if (!this.ctx || !this.busGains || !this.noiseBuffer) return;
    if (this.ctx.state !== 'running') return;

    if (options.throttleMs) {
      const now = performance.now();
      const last = this.lastPlayedAt.get(id) ?? -Infinity;
      if (now - last < options.throttleMs) return;
      this.lastPlayedAt.set(id, now);
    }

    const ctx = this.ctx;
    const sfx = this.busGains.sfx;
    const noise = this.noiseBuffer;

    switch (id) {
      // --- 足音 ---------------------------------------------------
      case 'footstep_carpet':
        playNoise(ctx, sfx, noise, {
          duration: 0.11,
          center: 420 + Math.random() * 90,
          q: 0.7,
          gain: 0.1,
          filter: 'lowpass',
        });
        break;
      case 'footstep_tile':
        playNoise(ctx, sfx, noise, {
          duration: 0.07,
          center: 1900 + Math.random() * 500,
          q: 1.4,
          gain: 0.11,
        });
        playTone(ctx, sfx, { freq: 150, endFreq: 90, type: 'sine', gain: 0.05, decay: 0.06 });
        break;
      case 'footstep_stair':
        playNoise(ctx, sfx, noise, {
          duration: 0.09,
          center: 1200 + Math.random() * 400,
          q: 1.1,
          gain: 0.12,
        });
        break;

      // --- 操作音 -------------------------------------------------
      case 'examine':
        playTone(ctx, sfx, { freq: 620, endFreq: 880, type: 'sine', gain: 0.18, decay: 0.1 });
        break;
      case 'confirm':
        playTone(ctx, sfx, { freq: 660, type: 'triangle', gain: 0.2, decay: 0.09 });
        playTone(ctx, sfx, { freq: 990, type: 'triangle', gain: 0.15, decay: 0.12, delay: 0.06 });
        break;
      case 'cancel':
        playTone(ctx, sfx, { freq: 420, endFreq: 240, type: 'triangle', gain: 0.18, decay: 0.14 });
        break;
      case 'ui_hover':
        playTone(ctx, sfx, { freq: 1200, type: 'sine', gain: 0.05, decay: 0.05 });
        break;
      case 'notebook_open':
        playNoise(ctx, sfx, noise, { duration: 0.16, center: 900, q: 0.8, gain: 0.13 });
        playTone(ctx, sfx, { freq: 300, endFreq: 520, type: 'sine', gain: 0.1, decay: 0.14 });
        break;
      case 'notebook_close':
        playNoise(ctx, sfx, noise, { duration: 0.14, center: 700, q: 0.8, gain: 0.13 });
        playTone(ctx, sfx, { freq: 520, endFreq: 260, type: 'sine', gain: 0.1, decay: 0.14 });
        break;
      case 'door':
        playNoise(ctx, sfx, noise, { duration: 0.3, center: 300, q: 0.5, gain: 0.1, filter: 'lowpass' });
        break;

      // --- 機器音 -------------------------------------------------
      case 'cable_click':
        // ツメが掛かる「カチッ」。短い高域ノイズ＋矩形波の一撃
        playNoise(ctx, sfx, noise, { duration: 0.035, center: 3200, q: 3, gain: 0.3 });
        playTone(ctx, sfx, {
          freq: 2100,
          endFreq: 900,
          type: 'square',
          gain: 0.12,
          attack: 0.002,
          decay: 0.05,
        });
        break;
      case 'link_up':
        // リンクアップの「ピッ」。2 音の上がり
        playTone(ctx, sfx, { freq: 1046, type: 'square', gain: 0.14, decay: 0.07, lowpass: 4000 });
        playTone(ctx, sfx, {
          freq: 1568,
          type: 'square',
          gain: 0.14,
          decay: 0.12,
          delay: 0.08,
          lowpass: 4000,
        });
        break;
      case 'warning':
        for (let i = 0; i < 3; i++) {
          playTone(ctx, sfx, {
            freq: 880,
            type: 'square',
            gain: 0.13,
            decay: 0.09,
            delay: i * 0.16,
            lowpass: 3000,
          });
        }
        break;
      case 'error':
        playTone(ctx, sfx, { freq: 300, endFreq: 160, type: 'sawtooth', gain: 0.16, decay: 0.22, lowpass: 1400 });
        break;

      // --- 端末 ---------------------------------------------------
      case 'terminal_type':
        playNoise(ctx, sfx, noise, {
          duration: 0.022,
          center: 2400 + Math.random() * 1400,
          q: 4,
          gain: 0.07,
        });
        break;
      case 'terminal_done':
        playTone(ctx, sfx, { freq: 880, type: 'sine', gain: 0.12, decay: 0.1 });
        break;

      // --- 通知・達成 ---------------------------------------------
      case 'clue_found':
        playArpeggio(ctx, sfx, [784, 1046], { step: 0.08, gain: 0.15, decay: 0.22 });
        break;
      case 'notify':
        playArpeggio(ctx, sfx, [880, 1174], { step: 0.1, gain: 0.16, decay: 0.25 });
        break;
      case 'chapter_clear':
        // C - E - G - C の分散和音
        playArpeggio(ctx, sfx, [523, 659, 784, 1046], {
          step: 0.13,
          gain: 0.2,
          decay: 0.45,
          type: 'triangle',
        });
        playTone(ctx, sfx, {
          freq: 261,
          type: 'sine',
          gain: 0.12,
          decay: 0.9,
          delay: 0.52,
        });
        break;

      default: {
        const exhaustive: never = id;
        console.warn('[audio] 未知の音 ID', exhaustive);
      }
    }
  }

  /** 文字列からの再生（データ側の playSound エフェクト用。未知の ID は無視する） */
  playById(id: string): void {
    if (KNOWN_SOUNDS.has(id)) {
      this.play(id as SoundId);
    } else {
      console.warn(`[audio] 未定義の soundId です: ${id}`);
    }
  }

  dispose(): void {
    this.stopAmbience();
    if (this.ctx) void this.ctx.close().catch(() => undefined);
    this.ctx = null;
    this.masterGain = null;
    this.busGains = null;
  }
}

export const KNOWN_SOUNDS: ReadonlySet<string> = new Set<SoundId>([
  'footstep_carpet',
  'footstep_tile',
  'footstep_stair',
  'examine',
  'confirm',
  'cancel',
  'notebook_open',
  'notebook_close',
  'cable_click',
  'link_up',
  'warning',
  'error',
  'terminal_type',
  'terminal_done',
  'notify',
  'chapter_clear',
  'clue_found',
  'ui_hover',
  'door',
]);
